// M12.11 — per-page document.title + meta description.
//
// SPA caveat: OG / Twitter scrapers only see the static `<meta>` baked
// into index.html (they don't execute our React). This hook is for the
// in-browser experience — tab labels, bookmark names, the user's
// history — where React mutations DO reach the user. Per-page OG
// imagery would need SSR or a prerender-route setup, which is out of
// scope for M12.11.

import { useEffect } from "react";

export interface PageMetaOptions {
  /** Full tab title. Convention: "{Page} · Traffic Snapshot". */
  title: string;
  /** Optional description override. When omitted, the index.html
   *  baseline description stays in place. */
  description?: string;
}

function setMetaContent(name: string, content: string): void {
  let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", name);
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
}

// Captured once on first module load so navigating between pages — where
// only some use this hook with a description — restores the baseline
// from index.html instead of leaving the previous page's description in
// the DOM.
const BASELINE_DESCRIPTION =
  typeof document !== "undefined"
    ? (document
        .querySelector<HTMLMetaElement>('meta[name="description"]')
        ?.getAttribute("content") ?? "")
    : "";

export function usePageMeta({ title, description }: PageMetaOptions): void {
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.title = title;
    setMetaContent("description", description ?? BASELINE_DESCRIPTION);
  }, [title, description]);
}
